import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, ArrowDownToLine, FileText, Image, Library } from "lucide-react";
import { useParams } from "react-router-dom";
import { API, http, requestErrorMessage } from "@/lib/erp";
import { catalogueFileKind } from "@/lib/catalogue";
import StatusAvatar from "@/components/StatusAvatar";

function publicError(error) {
  const status = error?.response?.status;
  if (status === 404) return "This public catalogue link was not found. It may have been removed or revoked.";
  if (status === 410) return "This public catalogue link has expired.";
  return requestErrorMessage(error, "load this shared file");
}

export default function CatalogueShared() {
  const { token } = useParams();
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setMetadata(null);
    http.get(`/public/catalogue/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (!data || typeof data !== "object" || !data.content_type || !data.filename) {
          throw new Error("The shared file details could not be read.");
        }
        if (active) setMetadata(data);
      })
      .catch((requestError) => { if (active) setError(requestError instanceof Error && !requestError.response ? requestError.message : publicError(requestError)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const fileUrl = metadata ? `${API}/public/catalogue/${encodeURIComponent(token)}/file` : "";
  const kind = catalogueFileKind(metadata?.content_type);
  useEffect(() => {
    if (!metadata || kind === "unknown") {
      setPreviewUrl("");
      setPreviewError("");
      setPreviewLoading(false);
      return undefined;
    }
    let active = true;
    let ownedUrl = "";
    const controller = new AbortController();
    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(true);
    axios.get(fileUrl, { responseType: "blob", signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        ownedUrl = URL.createObjectURL(data);
        setPreviewUrl(ownedUrl);
      })
      .catch((requestError) => {
        if (active) setPreviewError(publicError(requestError));
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    };
  }, [fileUrl, kind, metadata]);
  const expiry = metadata?.expires_at
    ? new Date(metadata.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "";

  return <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 sm:py-12">
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand text-brand-foreground"><Library size={20} /></div>
        <div><p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-700">VGRUPS</p><p className="text-sm font-medium text-slate-600">Shared catalogue file</p></div>
      </header>
      {loading ? <section className="grid min-h-[55vh] place-items-center rounded-2xl border border-slate-200 bg-white shadow-sm"><StatusAvatar label="Loading shared file…" /></section>
        : error ? <section className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-10">
          <StatusAvatar type="ghost" state="sleeping" size={72} />
          <h1 className="mt-4 inline-flex items-center gap-2 text-xl font-semibold text-slate-900"><AlertCircle size={20} className="text-rose-600" />File unavailable</h1>
          <p role="alert" className="mt-2 text-sm leading-6 text-slate-600">{error}</p>
        </section> : metadata && <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-7">
            <div className="flex min-w-0 gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-700">{kind === "image" ? <Image size={20} /> : <FileText size={20} />}</div>
              <div className="min-w-0"><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Public file</p><h1 className="mt-1 break-words text-xl font-bold text-slate-900 sm:text-2xl">{metadata.title || metadata.filename}</h1><p className="mt-1 break-all text-sm text-slate-500">{metadata.filename}</p>
                {metadata.description && <p className="mt-3 max-w-3xl whitespace-pre-line text-sm leading-6 text-slate-600">{metadata.description}</p>}
                {expiry && <p className="mt-3 text-xs text-slate-500">This public link expires {expiry}.</p>}
              </div>
            </div>
            <a className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground shadow-sm hover:opacity-90" href={`${fileUrl}?download=true`}><ArrowDownToLine size={16} /> Download file</a>
          </div>
          <div className="bg-slate-100 p-2 sm:p-5">
            {previewError && <div className="grid min-h-[30vh] place-items-center rounded-lg border border-rose-200 bg-white p-6 text-center"><div><AlertCircle className="mx-auto h-8 w-8 text-rose-600" /><h2 className="mt-3 font-semibold text-slate-900">File unavailable</h2><p role="alert" className="mt-1 max-w-lg text-sm leading-6 text-slate-600">{previewError}</p></div></div>}
            {previewLoading && <StatusAvatar type="drop" label="Loading preview…" className="h-[55vh] min-h-[350px] rounded-lg bg-white" />}
            {!previewLoading && !previewError && previewUrl && kind === "pdf" && <iframe title={`PDF preview: ${metadata.title || metadata.filename}`} src={previewUrl} className="h-[72vh] min-h-[420px] w-full rounded-lg border border-slate-200 bg-white" />}
            {!previewLoading && !previewError && previewUrl && kind === "image" && <div className="grid min-h-[40vh] place-items-center overflow-auto rounded-lg bg-white p-3"><img src={previewUrl} alt={metadata.title || metadata.filename} className="max-h-[72vh] max-w-full object-contain" /></div>}
            {kind === "unknown" && <div className="grid min-h-[30vh] place-items-center rounded-lg bg-white p-6 text-center"><div><FileText className="mx-auto h-8 w-8 text-slate-400" /><p className="mt-3 text-sm text-slate-600">This file type cannot be previewed in your browser.</p><a className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:underline" href={`${fileUrl}?download=true`}><ArrowDownToLine size={15} /> Download to view</a></div></div>}
          </div>
        </article>}
      <p className="mt-5 text-center text-xs text-slate-400">This link grants access to the selected file only.</p>
    </div>
  </main>;
}